"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { signOut, useSession } from "next-auth/react";
import { LogOut, Menu, Settings, Shield, ShoppingBag, ShoppingCart, User, X } from "lucide-react";
import { primaryNavLinks } from "./data";

// isAuthenticated is only passed by pages that already fetched the session
// server-side for other reasons (e.g. /account). Public catalog/marketing
// pages omit it so they can skip the auth() call themselves - a cookies()
// read forces the whole page to render dynamically on every request - and
// this component resolves the login state itself, client-side, instead.
export function SiteHeader({ isAuthenticated }: { isAuthenticated?: boolean }) {
  const { data: session, status, update } = useSession();
  const resolvedIsAuthenticated = isAuthenticated ?? !!session?.user;
  const isAdmin = session?.user?.role === "admin";

  // Login happens through a Server Action (see app/login/actions.ts), which
  // signs the user in and sets the cookie server-side without ever going
  // through next-auth/react's client signIn() - so SessionProvider's client
  // cache never learns about it and stays stuck on whatever it resolved on
  // the /login page (unauthenticated). Pages that already know the real
  // state from the server (isAuthenticated=true, e.g. /account right after
  // login) use this to force the client cache to catch up, so navigating
  // onward to a page that relies on useSession() (no isAuthenticated prop)
  // doesn't show a false "Zaloguj się".
  useEffect(() => {
    if (isAuthenticated && status === "unauthenticated") {
      update();
    }
  }, [isAuthenticated, status, update]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [accountMenuOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  function openAccountMenu() {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setAccountMenuOpen(true);
  }

  function scheduleCloseAccountMenu() {
    closeTimeoutRef.current = setTimeout(() => setAccountMenuOpen(false), 150);
  }

  // Client-side signOut (not a Server Action form) so next-auth/react
  // immediately clears the SessionProvider's cached session and broadcasts
  // the sign-out to other open tabs, instead of leaving the header stuck
  // showing "Moje konto" until a manual page refresh.
  function handleSignOut() {
    signOut({ redirectTo: "/" });
  }

  return (
    <header
      className={`sticky top-0 z-50 py-3 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        isScrolled ? "bg-white/95 shadow-sm backdrop-blur-md" : "bg-white"
      }`}
    >
      <div className="mx-auto max-w-screen-xl px-6">
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="p-2 text-zinc-800 xl:hidden"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <nav className="hidden items-center gap-2 text-[13px] font-bold tracking-wider xl:flex">
            {primaryNavLinks.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#fdf0f4] px-3.5 py-1.5 uppercase text-[#e0507a] transition-colors hover:bg-[#e0507a] hover:text-white"
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </Link>
            ))}
          </nav>

          <Link href="/" className="shrink-0">
            <Image
              src="/images/logo.png"
              alt="Malindra Travel"
              width={700}
              height={671}
              priority
              className="h-14 w-auto"
            />
          </Link>

          <nav className="hidden items-center gap-3 text-[13px] font-medium xl:flex">
            {resolvedIsAuthenticated ? (
              <div
                className="relative"
                ref={accountMenuRef}
                onMouseEnter={openAccountMenu}
                onMouseLeave={scheduleCloseAccountMenu}
              >
                <button
                  type="button"
                  aria-expanded={accountMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setAccountMenuOpen((open) => !open)}
                  onFocus={openAccountMenu}
                  className="flex items-center gap-2 rounded-full border border-[#e0507a] px-5 py-2 text-sm font-bold text-[#e0507a] transition-all hover:bg-[#e0507a] hover:text-white"
                >
                  Moje konto <User className="h-4 w-4" />
                </button>

                {accountMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-2xl bg-white py-2 shadow-lg"
                  >
                    {isAdmin && (
                      <Link
                        href="/admin"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-[#fdf0f4] hover:text-[#e0507a]"
                      >
                        <Shield className="h-4 w-4" />
                        Panel admina
                      </Link>
                    )}
                    <Link
                      href="/account"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-[#fdf0f4] hover:text-[#e0507a]"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Moje zamówienia
                    </Link>
                    <Link
                      href="/account/settings"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-[#fdf0f4] hover:text-[#e0507a]"
                    >
                      <Settings className="h-4 w-4" />
                      Zarządzaj kontem
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors hover:bg-[#fdf0f4] hover:text-[#e0507a]"
                    >
                      <LogOut className="h-4 w-4" />
                      Wyloguj się
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="flex items-center gap-2 rounded-full border border-[#e0507a] px-5 py-2 text-sm font-bold text-[#e0507a] transition-all hover:bg-[#e0507a] hover:text-white"
              >
                Zaloguj się <User className="h-4 w-4" />
              </Link>
            )}
            <Link
              href="/ebooks"
              className="flex items-center gap-2 rounded-full bg-[#e0507a] px-5 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#c9436a]"
            >
              Sklep <ShoppingCart className="h-4 w-4" />
            </Link>
          </nav>

          <div className="flex items-center gap-2 xl:hidden">
            <Link
              href={resolvedIsAuthenticated ? "/account" : "/login"}
              className="rounded-full border border-[#e0507a] p-2 text-[#e0507a]"
              aria-label={resolvedIsAuthenticated ? "Moje konto" : "Zaloguj się"}
            >
              <User className="h-4 w-4" />
            </Link>
            <Link
              href="/ebooks"
              className="rounded-full bg-[#e0507a] p-2 text-white"
              aria-label="Sklep"
            >
              <ShoppingCart className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <>
          <div
            aria-hidden
            className="animate-backdrop-in motion-reduce:animate-none fixed inset-0 z-40 bg-zinc-900/25 xl:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            id="mobile-menu"
            className="animate-menu-in motion-reduce:animate-none absolute inset-x-0 top-full z-50 flex w-full flex-col gap-2 rounded-b-3xl border-t border-[#e0507a]/10 bg-white px-6 py-6 shadow-lg xl:hidden"
          >
            {primaryNavLinks.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className="flex items-center gap-3 rounded-full bg-[#fdf0f4] px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#e0507a] transition-colors active:bg-[#e0507a] active:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon className="h-4 w-4 shrink-0" /> {label}
              </Link>
            ))}

            <div className="mt-2 flex flex-col gap-2 border-t border-[#e0507a]/10 pt-4">
              {resolvedIsAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link
                      href="/admin"
                      className="flex items-center gap-3 rounded-full px-4 py-3 text-sm font-bold text-zinc-700 transition-colors active:bg-[#fdf0f4] active:text-[#e0507a]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Shield className="h-4 w-4 shrink-0" /> Panel admina
                    </Link>
                  )}
                  <Link
                    href="/account"
                    className="flex items-center gap-3 rounded-full px-4 py-3 text-sm font-bold text-zinc-700 transition-colors active:bg-[#fdf0f4] active:text-[#e0507a]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ShoppingBag className="h-4 w-4 shrink-0" /> Moje zamówienia
                  </Link>
                  <Link
                    href="/account/settings"
                    className="flex items-center gap-3 rounded-full px-4 py-3 text-sm font-bold text-zinc-700 transition-colors active:bg-[#fdf0f4] active:text-[#e0507a]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Settings className="h-4 w-4 shrink-0" /> Zarządzaj kontem
                  </Link>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 rounded-full px-4 py-3 text-left text-sm font-bold text-zinc-700 transition-colors active:bg-[#fdf0f4] active:text-[#e0507a]"
                  >
                    <LogOut className="h-4 w-4 shrink-0" /> Wyloguj się
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  className="flex items-center gap-3 rounded-full px-4 py-3 text-sm font-bold text-zinc-700 transition-colors active:bg-[#fdf0f4] active:text-[#e0507a]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="h-4 w-4 shrink-0" /> Zaloguj się
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
